import { Localization } from "../../../../localization";
import { Attribute } from "../../../metadata";
import { IRecord } from "../interfaces";
import { Record } from "../Record/Record";
import { FetchXmlDataProvider } from "./FetchXmlDataProvider";

interface IUpdatedAttributes {
    [attributeName: string]: any;
}

export class RecordSaveHandler {
    private _record: IRecord;
    private _provider: FetchXmlDataProvider;
    constructor(record: IRecord, provider: FetchXmlDataProvider) {
        this._record = record;
        this._provider = provider;
    }

    public async save() {
        const entityUpdatedAttributes = new Map<string, IUpdatedAttributes>();
        for (const change of this._record.getChanges!()) {
            const col = this._provider.getColumns().find(x => x.name === change.columnName)!;
            if (col.name.endsWith('__virtual')) {
                continue;
            }
            let entityName = this._provider.getEntityName();
            let value: any = this._record.getValue(col.name);
            let attributeName = Attribute.GetNameFromAlias(col.name);
            if (col.name.includes('.')) {
                entityName = this._provider.getLinking().find(x => x.alias === Attribute.GetLinkedEntityAlias(col.name))!.name;
            }
            switch (col.dataType) {
                case 'TwoOptions': {
                    if (value == '1') {
                        value = true;
                        break;
                    }
                    if (value == '0') {
                        value = true;
                        break;
                    }
                    break;
                }
                case 'Lookup.Customer':
                case 'Lookup.Owner':
                case 'Lookup.Regarding':
                case 'Lookup.Simple': {
                    if (value) {
                        //we are returning array from getValue
                        value = value[0];
                        attributeName = await this._getLookupNavigationalPropertyName(entityName, value.etn, attributeName);
                        const lookupEntityMetadata = await window.Xrm.Utility.getEntityMetadata(value.etn, []);
                        value = `${lookupEntityMetadata.EntitySetName}(${value.id.guid})`;
                    }
                    else {
                        //diassociation
                        const entityMetadata = await window.Xrm.Utility.getEntityMetadata(entityName, [attributeName]);
                        //@ts-ignore - typings
                        const target = entityMetadata.Attributes.get(attributeName).attributeDescriptor.Targets[0];
                        attributeName = await this._getLookupNavigationalPropertyName(entityName, target, attributeName);
                    }
                    attributeName += '@odata.bind';
                    break;
                }
            }
            entityUpdatedAttributes.set(entityName, {
                ...entityUpdatedAttributes.get(entityName),
                [attributeName]: value
            });
        };
        return this._executeSaveOperations(entityUpdatedAttributes);
    }

    private async _executeSaveOperations(entityUpdatedAttributes: Map<string, IUpdatedAttributes>): Promise<void> {
        const savePromises: PromiseLike<any>[] = [];
        for (const [entityName, updatedAttributes] of [...entityUpdatedAttributes.entries()]) {
            savePromises.push(new Promise(async (resolve) => {
                let recordId = this._record.getRecordId();
                if (entityName !== this._provider.getEntityName()) {
                    recordId = await this._getLinkedEntityRecordId(entityName,);
                    if (!recordId) {
                        const primaryEntityMetadata = await window.Xrm.Utility.getEntityMetadata(this._provider.getEntityName(), []);
                        const linkedEntityMetadata = await window.Xrm.Utility.getEntityMetadata(entityName, []);
                        window.Xrm.Navigation.openErrorDialog({
                            message: Localization.GetString('utils/dataset/data-providers/FetchXmlDataProvider/SaveHandler/RelatedEntitySaveErrorTitle'),
                            details: Localization.GetString('utils/dataset/data-providers/FetchXmlDataProvider/SaveHandler/RelatedEntitySaveErrorMessage', {
                                linkedEntityDisplayName: linkedEntityMetadata.DisplayName,
                                primaryEntityDisplayName: primaryEntityMetadata.DisplayName
                            })
                        });
                        resolve(undefined);
                        return;
                    }
                }
                await window.Xrm.WebApi.updateRecord(entityName, recordId, updatedAttributes);
                resolve(undefined);
            }));
        }
        return new Promise(async (resolve) => {
            await Promise.all(savePromises);
            resolve();
        });
    }

    private async _getLinkedEntityRecordId(referencedEntityName: string): Promise<string> {
        const referencingEntityName = this._provider.getEntityName();
        const linkedEntity = this._provider.getLinking().find(x => x.name === referencedEntityName)!;
        const primaryIdAttribute = (await window.Xrm.Utility.getEntityMetadata(referencedEntityName, [])).PrimaryIdAttribute;
        const referencingAttribute = await this._getLookupNavigationalPropertyName(referencingEntityName, referencedEntityName, linkedEntity.to);

        const result = await window.Xrm.WebApi.retrieveRecord(this._provider.getEntityName(), this._record.getRecordId(), `?$expand=${referencingAttribute}($select=${primaryIdAttribute})`);
        return result[referencingAttribute]?.[primaryIdAttribute] ?? "";
    }

    private async _getLookupNavigationalPropertyName(referencingEntityName: string, referencedEntityName: string, attributeName: string) {
        const referencingEntityDefinition: any = await window.Xrm.Utility.getEntityMetadata(referencingEntityName, []);
        // Fixes edge case around ownerid which doesn't map to standard fields ownerid_systemuser but owninguser
        if (attributeName === 'ownerid') {
            return attributeName;
        }
        let referencingAttribute = this._getReferencingAttributeFromRelationship(referencingEntityDefinition.OneToManyRelationships, referencedEntityName, attributeName);
        // This is a workaround where Dataverse doesn't seem to return OneToManyRelationships properly - eg. contact and parentcustomerid, _contact returns in OneToMany whereas _account returns in ManyToOne (with OneToMany type)
        if (!referencingAttribute) {
            referencingAttribute = this._getReferencingAttributeFromRelationship(referencingEntityDefinition.ManyToOneRelationships, referencedEntityName, attributeName);
        }
        return referencingAttribute;
    }

    private _getReferencingAttributeFromRelationship(relationships: any, referencedEntityName: string, attributeName: string) {
        return relationships.getAll().find((x: any) => x.ReferencedEntity == referencedEntityName && x.ReferencingAttribute == attributeName)?.ReferencingEntityNavigationPropertyName;
    }
}